import React from 'react';
import connectToStores from 'alt-utils/lib/connectToStores';
import {CallCard} from '@components/call-card/CallCard';
import CallCardStore from '@store/call-card/CallCardStore';
import {List, Map} from 'immutable';
import Loading from 'components/pure/utils/Loading';
import {FileUploadId} from 'records/CoachingReviewFileUpload';
import CallCardActions from '@store/call-card/CallCardActions';
import {Section} from '@store/call-card/CallCard.types';
import {RouterLocation} from '@store/@types/util/ReactRouter';
import AuthStore from 'stores/AuthStore';
import LibraryStore from 'stores/library/LibraryStore';
import LibraryActions from 'actions/library/LibraryActions';
import FileUploadStore from 'stores/files/FileUploadStore';
import {CallCardVariant} from '@components/call-card/CallCard.types';
import CrmSupportsCloudStore from 'stores/crm/CrmSupportsCloudStore';
import CrmSyncActions from 'actions/crm/CrmSyncActions';
import {sortCallSpeakers} from './CallSpeaker.utils';
import {filterTopics} from './TopicKeyword.utils';
import {TopicFilter} from '@store/topic-keyword/TopicKeyword.types';
import ToastActions from '@store/toast/ToastActions';
import DirectReportsActions from 'actions/coaching/DirectReportsActions';
import DirectReportsStore from 'stores/coaching/DirectReportsStore';
import {scrollToTop} from 'utils/scrollToTop';
import {joinSharedAreas, SharedAreas} from '@components/call-card/SharedHighlights.utils';

interface CallCardContainerProps {
  callId: FileUploadId;
  location: RouterLocation;
  variant: CallCardVariant;
  libraryId?: string | null;
}

interface CallCardContainerState {}

@connectToStores
class CallCardContainer extends React.PureComponent<
  CallCardContainerProps & ReturnType<typeof CallCardContainer.getPropsFromStores>,
  CallCardContainerState
> {
  static getStores() {
    return [
      CallCardStore,
      AuthStore,
      LibraryStore,
      FileUploadStore,
      CrmSupportsCloudStore,
      DirectReportsStore,
    ];
  }

  static getPropsFromStores({callId, variant}) {
    const {
      calls,
      callAssets,
      scorecards,
      speakers,
      userActivities,
      transcripts,
      segments,
      talkTimes,
      comments,
      topics,
      activeTopics,
      matches,
      topicFilters,
    } = CallCardStore.getState();
    const sections = List<Section>(); // TODO
    const sessionUser = AuthStore.getState().user;
    // TODO Replace with better store/api
    const {sources, fileHighlights} = LibraryStore.getState();
    const callTags = FileUploadStore.getState().callTags;
    const supportsCrmSync = CrmSupportsCloudStore.getState().get('supportsCrmSync');

    const call = calls.get(callId);
    const callTalkTime = talkTimes.get(callId, Map());
    const topicMatches = matches.get(callId, Map());
    const topicFilter = topicFilters.get(callId, TopicFilter.EITHER);

    const callSpeakers = sortCallSpeakers(speakers.get(callId, List()), call, callTalkTime);

    // Filter topic matches for internal/external speakers
    const filteredTopicMatches = filterTopics(topicMatches, callSpeakers, topicFilter);

    const {directReports} = DirectReportsStore.getState();

    /**
     * TODO Create API to serve this information as part of call card store, or atleast using
     * the normal UserComment model
     */
    const sharedAreas =
      variant === CallCardVariant.LIBRARY && call?.duration
        ? joinSharedAreas(
            fileHighlights.map(h => {
              return [h.highlightStart * call.duration, h.highlightEnd * call.duration];
            })
          )
        : (List() as SharedAreas);

    return {
      call,
      callAsset: callAssets.get(callId),
      topics: topics.get(callId, List()),
      activeTopics: activeTopics.get(callId, Map()),
      sections,
      speakers: callSpeakers,
      scorecards: scorecards.get(callId, List()),
      userActivities: userActivities.get(callId, List()),
      transcript: transcripts.get(callId, List()),
      segments: segments.get(callId, List()),
      talkTime: callTalkTime,
      sessionUser,
      tags: callTags.get(callId, List()),
      libraries: sources.libraries ?? [],
      comments: comments.get(callId, Map()),
      matches: filteredTopicMatches,
      supportsCrmSync,
      topicFilter,
      directReports: directReports.map(u => u.id).toSet(),
      sharedAreas,
      fileHighlights,
    };
  }

  componentDidMount() {
    const {
      call,
      callId,
      callAsset,
      scorecards,
      speakers,
      userActivities,
      transcript,
      topics,
      segments,
      comments,
      sessionUser,
      directReports,
      libraryId,
    } = this.props;
    scrollToTop();

    setTimeout(() => {
      call || CallCardActions.getCall(callId);
      callAsset || CallCardActions.getCallSource(callId);
      scorecards.isEmpty() && CallCardActions.getScorecards(callId);
      speakers.isEmpty() && CallCardActions.getSpeakers(callId);
      userActivities.isEmpty() && CallCardActions.getUserCallActivity(callId);
      transcript.isEmpty() && CallCardActions.getTranscript(callId);
      segments.isEmpty() && CallCardActions.getSegments(callId);
      comments.isEmpty() && CallCardActions.getComments(callId);
      topics.isEmpty() && CallCardActions.getTopics(callId, List());
      sessionUser.isTeamManager &&
        directReports.isEmpty() &&
        DirectReportsActions.directReports(sessionUser.id);
      LibraryActions.getTags();
      LibraryActions.getLibraries();
      libraryId && LibraryActions.fetchLibraryHighlights(libraryId, callId);
      CrmSyncActions.crmSupportsCloud();
    });
  }

  componentWillUnmount() {
    // Clear topics, topic matches, and active topics
    CallCardActions.resetTopics(this.props.callId);
    CallCardActions.resetMatches();
    // Clear toasts
    ToastActions.removeAllToasts({dismissibleOnly: true});
  }

  render() {
    if (!this.props.call) {
      return <Loading />;
    }

    return <CallCard {...this.props} call={this.props.call} />;
  }
}

export default CallCardContainer as unknown as React.FC<CallCardContainerProps>;
